import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, Star, QrCode, Activity, CheckCircle, RefreshCw, 
  Brain, Sparkles, AlertTriangle, ArrowRight, FileText, Zap, 
  ShieldAlert, Check, Archive, ClipboardList, AlertOctagon, HeartPulse, Target, HelpCircle, ChevronDown 
} from 'lucide-react';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase'; 

// 🚀 1. PDR KARAR DESTEK MOTORU (TEST_REGISTRY VE DOMAIN BİLGİSİ)
const TEST_REGISTRY = {
    'dass_21': { type: 'risk', engine: 'universal', weight: 1.0, domain: 'psychological' },
    'yasam_pencerem': { type: 'risk', engine: 'universal', specialHandler: 'yasamPencerem', weight: 1.0, domain: 'psychological' },
    'rosenberg_benlik_saygisi': { type: 'risk', engine: 'custom', weight: 1.0, domain: 'psychological' },
'liebowitz_sosyal_kaygi': { type: 'risk', engine: 'legacy', weight: 0.9, domain: 'psychological' },

// 🆕 Beck Anksiyete → Duygusal & Kişisel Alan
'beck_anksiyete': { type: 'risk', engine: 'custom', weight: 1.0, domain: 'psychological' },

'sinav_kaygisi': { type: 'risk', engine: 'universal', weight: 0.8, domain: 'academic' },
    'basarisizlik_nedenleri': { type: 'risk', engine: 'universal', weight: 0.9, domain: 'academic' },
    'devamsizlik_nedenleri': { type: 'risk', engine: 'universal', weight: 0.9, domain: 'behavioral' },
    'snap_iv_form': { type: 'risk', engine: 'legacy', weight: 0.9, domain: 'behavioral' },
    'asrs_form': { type: 'risk', engine: 'legacy', weight: 0.9, domain: 'behavioral' },
    'internet_bagimliligi': { type: 'risk', engine: 'legacy', weight: 0.8, domain: 'behavioral' }
};

 const InfoHint = ({ title, description, items = [] }) => (
  <div className="relative group inline-flex items-center">
    <button
      type="button"
      className="w-4 h-4 rounded-full border border-slate-300 text-slate-400 hover:text-slate-600 hover:border-slate-400 flex items-center justify-center transition-colors"
      aria-label={`${title} hakkında bilgi`}
    >
      <HelpCircle className="w-3 h-3" />
    </button>

    <div className="absolute left-1/2 -translate-x-1/2 top-6 z-50 hidden group-hover:block w-72 rounded-2xl bg-slate-900 text-white shadow-2xl p-3">
      <p className="text-[11px] font-bold mb-1">{title}</p>

      <p className="text-[11px] leading-relaxed text-slate-200 mb-2">
        {description}
      </p>

      {items.length > 0 && (
        <>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 font-bold">
            Bu alanı besleyen testler
          </p>

          <ul className="space-y-1">
            {items.map((item, i) => (
              <li key={i} className="text-[11px] text-slate-200 flex items-start gap-2">
                <span className="mt-[5px] w-1 h-1 rounded-full bg-slate-400"></span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  </div>
);

 const DOMAIN_INFO = {
  psychological: {
    title: "Duygusal & Kişisel Alan",
    description:
      "Bu alan, öğrencinin duygusal zorlanmaları, içsel iyi oluşu ve psikolojik destek ihtiyacına dair sinyalleri özetler. Bu oran tanı koymaz; yalnızca aktif takip gerektirebilecek işaretleri görünür hale getirir.",
   items: [
  "DASS-21",
  "Yaşam Pencerem",
  "Rosenberg Benlik Saygısı Ölçeği",
  "Liebowitz Sosyal Kaygı Ölçeği",
  "Beck Anksiyete Ölçeği"
]
  },

  academic: {
    title: "Eğitsel & Akademik Alan",
    description:
      "Bu alan, öğrencinin sınav süreci, akademik zorlanmaları ve öğrenme sürecinde ortaya çıkan risk sinyallerini özetler.",
    items: [
      "Sınav Kaygısı Ölçeği",
      "Başarısızlık Nedenleri Analizi"
    ]
  },

  behavioral: {
    title: "Sosyal & Davranışsal Alan",
    description:
      "Bu alan, dikkat, dürtüsellik, devam durumu, davranış örüntüleri ve teknoloji kullanımı gibi sosyal-davranışsal sinyalleri özetler.",
    items: [
      "SNAP-IV",
      "ASRS",
      "Devamsızlık Analizi",
      "İnternet Bağımlılığı Ölçeği"
    ]
  }
};

// Basitleştirilmiş Dashboard Gümrük Motoru
const quickNormalize = (sub) => {
    const meta = TEST_REGISTRY[sub.testId] || { type: 'unknown', engine: null, weight: 0.3, domain: 'unknown' };
    if (meta.type !== 'risk') return { ...sub, riskLevel: 'none', severity: null };

    let riskLevel = sub.reportData?.riskLevel;
    let severity = sub.reportData?.severity || null;

    // Yaşam Pencerem 18. Madde Kontrolü (Hızlı Bypass)
    if (sub.testId === 'yasam_pencerem' && sub.reportData?.risks) {
        const hasFatal = sub.reportData.risks.some(r => {
            const text = typeof r === 'object' ? (r.text || "") : String(r);
            return (typeof r === 'object' && String(r.id) === "18") || text.toLowerCase().includes("yaşamaya değer") || text.toLowerCase().includes("hayatı");
        });
        if (hasFatal) severity = 'critical';
    }

    if (!riskLevel) {
        const sum = String(sub.reportData?.summary || "").toUpperCase();
        if (sum.includes('YÜKSEK') || sum.includes('KIRMIZI') || sum.includes('AĞIR') || sum.includes('BAĞIMLI')) riskLevel = 'high';
        else if (sum.includes('SARI') || sum.includes('ORTA')) riskLevel = 'medium';
        else if (sum.length > 0) riskLevel = 'low';
        else riskLevel = 'unknown';
    }

    return { ...sub, riskLevel, severity, domain: meta.domain, weight: meta.weight };
};

const DashboardHome = ({
  submissions = [],
  user = {},
  setView,
  onAnalyze,
  academicYear = '2026-2027'
}) => {

  const [allClasses, setAllClasses] = useState([]);
  const [dismissedIds, setDismissedIds] = useState([]); 
  const [showBurnout, setShowBurnout] = useState(false);
  const [showGhost, setShowGhost] = useState(false);
  const [showRadar, setShowRadar] = useState(false);
  const [showQuarantine, setShowQuarantine] = useState(false);
  // 👤 Koçluk uyarılarında anlık/optimistik kapatma
const [acknowledgedGhostIds, setAcknowledgedGhostIds] = useState([]);

  useEffect(() => {
    if (user?.uid) {

      const q = query(
        collection(db, "classes"),
        where("clinicianUid", "==", user.uid)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {

        const classesForYear = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(cls => {
            const classYear = cls.academicYear || '2025-2026';
            return classYear === academicYear;
          });

        setAllClasses(classesForYear);
      });

      return () => unsubscribe();
    }

  }, [user, academicYear]);

 const currentStudentIndex =
  useMemo(() => {

    const map = {};


    if (
      !Array.isArray(allClasses)
    ) {
      return map;
    }


    allClasses.forEach(
      classItem => {

        const className =
          String(
            classItem?.name ?? ''
          ).trim();


        const students =
          Array.isArray(
            classItem?.students
          )
            ? classItem.students
            : [];


        students.forEach(
          student => {

            const studentNo =
              String(
                student?.no ?? ''
              ).trim();


            if (!studentNo) {
              return;
            }


            // Öğrenci kimliği okul genelinde öğrenci numarasıdır.
            // Sınıf yalnızca güncel konum bilgisidir.
            map[studentNo] = {

              className,

              name:
                String(
                  student?.name ?? ''
                ).trim(),

              student
            };
          }
        );
      }
    );


    return map;

  }, [allClasses]);


const getCurrentStudentLocation =
  no => {

    const studentNo =
      String(
        no ?? ''
      ).trim();


    if (!studentNo) {
      return null;
    }


    return (
      currentStudentIndex[
        studentNo
      ] ||
      null
    );
  };


const getStudentName =
  no => {

    return (
      getCurrentStudentLocation(
        no
      )?.name ||
      ""
    );
  };

  const handleResolveRisk = async (subId) => {
      if (!subId) return;
      setDismissedIds(prev => [...prev, subId]); 
      try {
          await updateDoc(doc(db, "submissions", subId), { isRiskResolved: true });
      } catch (error) {
          console.error("Risk çözüldü işaretlenirken hata:", error);
      }
  };

  // 👤 KOÇLUK: Sistemden kopan öğrenciyi görüldü / takibe alındı olarak işaretle
const handleAcknowledgeGhost = async (student) => {
    const subId = student?.lastActivitySubId;
    if (!subId) return;

    // Ekrandan anında düşür
    setAcknowledgedGhostIds(prev =>
        prev.includes(subId) ? prev : [...prev, subId]
    );

    try {
        await updateDoc(
            doc(db, "submissions", String(subId)),
            {
                ghostAcknowledgedAt: Date.now()
            }
        );
    } catch (error) {
        console.error("Koçluk uyarısı kapatılırken hata:", error);

        // Firebase yazamazsa uyarıyı geri getir
        setAcknowledgedGhostIds(prev =>
            prev.filter(id => id !== subId)
        );
    }
};

const handleOpenRiskRadar = () => {
    setShowRadar(true);

    setTimeout(() => {
        document
          .getElementById('dashboard-risk-radar')
          ?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
          });
    }, 100);
};

 // 🚀 2. PDR ANALİZ MOTORU
  const dashboardData = useMemo(() => {
    const studentProfiles = {};
    const domains = {
        psychological: { activeCount: 0, totalRisk: 0, label: 'Duygusal & Kişisel', color: 'bg-purple-500' },
        academic: { activeCount: 0, totalRisk: 0, label: 'Eğitsel & Akademik', color: 'bg-blue-500' },
        behavioral: { activeCount: 0, totalRisk: 0, label: 'Sosyal & Davranışsal', color: 'bg-amber-500' }
    };

    // 🆕 ÖĞRENCİ BAZLI PSİKOSOSYAL PROFİL MOTORU
// Eski domains yapısına şimdilik dokunmuyoruz.
// Aynı öğrenci aynı alanda birden fazla test çözse bile yalnızca 1 kez sayılır.
const domainStats = {
    psychological: {
        evaluatedStudents: new Set(),
        followUpStudents: new Set(),
        highRiskStudents: new Set(),
        criticalStudents: new Set()
    },
    academic: {
        evaluatedStudents: new Set(),
        followUpStudents: new Set(),
        highRiskStudents: new Set(),
        criticalStudents: new Set()
    },
    behavioral: {
        evaluatedStudents: new Set(),
        followUpStudents: new Set(),
        highRiskStudents: new Set(),
        criticalStudents: new Set()
    }
};
    
    let pendingCount = 0;
let activeParticipants = new Set();

// 👥 Seçili eğitim öğretim yılında Sınıf Yönetimi'ne kayıtlı öğrenciler
const registeredStudents = new Set();

allClasses.forEach(
  cls => {

    const students =
      Array.isArray(
        cls?.students
      )
        ? cls.students
        : [];


    students.forEach(
      student => {

        const studentNo =
          String(
            student?.no ?? ''
          ).trim();


        // Öğrenci numarası olmayan
        // bozuk/eksik kayıtları sayma.
        if (!studentNo) {
          return;
        }


        // Sınıf öğrenci kimliğinin parçası değildir.
        registeredStudents.add(
          studentNo
        );
      }
    );
  }
);

// 🚀 YENİ: Akademik Takip ve Koçluk Değişkenleri
const academicActivity = {};
    const SIMDI = Date.now();
    const BES_GUN = 5 * 24 * 60 * 60 * 1000;

  submissions.forEach(s => {

  // Önce kaydın koçluk/akademik veri olup olmadığını belirle
  const isKocluk =
      s.studentClass === 'Koçluk' ||
      s.testId === 'gunluk_soru_takibi' ||
      s.testId === 'deneme_net_takibi' ||
      s.testId === 'weekly_plan' ||
      s.testId === 'ai_macro_plan';

  // Koçluk ilerleme kayıtları "analiz bekleyen envanter" değildir
  if (s.status === 'pending' && !isKocluk) {
      pendingCount++;
  }

  if (
      s.studentClass === 'Öğretmen' ||
      String(s.studentNo).includes('Veli')
  ) return;

  const participantStudentNo =
  String(
    s.studentNo ?? ''
  ).trim();


if (participantStudentNo) {
  activeParticipants.add(
    participantStudentNo
  );
}

if (isKocluk) {

    const coachingStudentNo =
      String(
        s.studentNo ?? ''
      ).trim();


    if (!coachingStudentNo) {
      return;
    }


    const currentLocation =
      getCurrentStudentLocation(
        coachingStudentNo
      );


    if (
      !academicActivity[
        coachingStudentNo
      ]
    ) {

      academicActivity[
        coachingStudentNo
      ] = {

        lastActivity: 0,

        lastActivitySubId: null,

        lastActivityAckAt: null,

        denemeler: [],

        toplamSoru: 0,

        name:
          currentLocation?.name ||
          "",

        class:
          currentLocation?.className ||
          ""
      };
    }

    /*
      Uzmanın Koçluk Panelinden gönderdiği weekly_plan / ai_macro_plan
      öğrencinin kendi aktivitesi değildir.

      Öğrenci tarafından oluşturulan gerçek aktiviteler:
      - günlük soru girişi
      - deneme girişi
      - öğrencinin kendi plan ilerlemesini kaydetmesi
    */
    const isStudentActivity =
        s.testId === 'gunluk_soru_takibi' ||
        s.testId === 'deneme_net_takibi' ||
        (
            (s.testId === 'weekly_plan' || s.testId === 'ai_macro_plan') &&
            s.studentClass !== 'Koçluk'
        );

    const activityTime = Number(s.createdAt || s.id || 0);

    if (
  isStudentActivity &&
  activityTime >
    academicActivity[
      coachingStudentNo
    ].lastActivity
) {

  academicActivity[
    coachingStudentNo
  ].lastActivity =
    activityTime;


  academicActivity[
    coachingStudentNo
  ].lastActivitySubId =
    s.firebaseId ||
    s.id;


  academicActivity[
    coachingStudentNo
  ].lastActivityAckAt =
    s.ghostAcknowledgedAt ||
    null;


  // Güncel sınıf listesi varsa onu esas al.
  // Öğrenci artık güncel listede yoksa,
  // eski submission sınıfını geriye uyumlu olarak koru.
  academicActivity[
    coachingStudentNo
  ].class =
    currentLocation
      ?.className ||
    s.studentClass ||
    "Sınıfsız";


  academicActivity[
    coachingStudentNo
  ].name =
    currentLocation
      ?.name ||
    s.studentName ||
    "";
}

          // Tükenmişlik Radarı İçin: Son 7 günün soru eforunu ölçüyoruz
          if (s.testId === 'gunluk_soru_takibi' && s.answers?.toplam) {
              if ((SIMDI - activityTime) <= 7 * 24 * 60 * 60 * 1000) {
                  academicActivity[
  coachingStudentNo
].toplamSoru +=
  Number(
    s.answers.toplam
  );
              }
          }

          // Tükenmişlik Radarı İçin: Deneme netlerindeki düşüşü okuyoruz
          if (s.testId === 'deneme_net_takibi' && s.answers) {
              const a = s.answers;
              const net = (Number(a.turkceD||0) - Number(a.turkceY||0)/4) + (Number(a.matematikD||0) - Number(a.matematikY||0)/4) + (Number(a.sosyalD||0) - Number(a.sosyalY||0)/4) + (Number(a.fenD||0) - Number(a.fenY||0)/4) + (Number(a.edebiyatD||0) - Number(a.edebiyatY||0)/4) + (Number(a.tarih1D||0) - Number(a.tarih1Y||0)/4) + (Number(a.cografya1D||0) - Number(a.cografya1Y||0)/4);
              academicActivity[
  coachingStudentNo
].denemeler.push({
  time:
    activityTime,

  net:
    net
});
          }
          return; // Akademik veriyi aşağıdaki psikolojik risk motoruna BİLDİRME
      }
      
     // Henüz analiz edilmemiş envanterlerin yeri "Bekleyen Envanter" sayacıdır.
// Risk sonucu oluşmadan "Kontrol Gereken Veri" olarak değerlendirme.
if (s.status === 'pending') return;

const normTest = quickNormalize(s);

// Profil / kariyer / koçluk vb. risk testi olmayan kayıtları dışarıda bırak
if (normTest.riskLevel === 'none') return;

const normalizedStudentNo =
  String(
    s.studentNo ?? ''
  ).trim();


if (!normalizedStudentNo) {
  return;
}


const currentLocation =
  getCurrentStudentLocation(
    normalizedStudentNo
  );


const resolvedStudentClass =
  currentLocation
    ?.className ||
  s.studentClass ||
  "Sınıfsız";


const resolvedStudentName =
  currentLocation
    ?.name ||
  s.studentName ||
  "";


// Öğrenci sınıf değiştirse de aynı risk profiline bağlanır.
const key =
  normalizedStudentNo;

// 🆕 1. DEĞERLENDİRİLEN ÖĞRENCİLER
// Risk kaydı daha sonra "çözüldü" olarak işaretlenmiş olsa bile,
// öğrenci bu alanda değerlendirilmiş olduğu için paydada kalır.
const hasUsableRiskResult =
    ['low', 'medium', 'high'].includes(normTest.riskLevel) ||
    normTest.severity === 'critical';

if (domainStats[normTest.domain] && hasUsableRiskResult) {
    domainStats[normTest.domain].evaluatedStudents.add(key);
}

// Çözülmüş / işlem yapılmış riskler aktif sinyal sayılarına girmez
if (
    s.isRiskResolved ||
    dismissedIds.includes(s.firebaseId || s.id)
) {
    return;
}

// 🆕 2. AKTİF TAKİP SİNYALLERİ
if (domainStats[normTest.domain]) {

    // Orta, yüksek veya kritik kayıt = takip sinyali
    if (
        normTest.riskLevel === 'medium' ||
        normTest.riskLevel === 'high' ||
        normTest.severity === 'critical'
    ) {
        domainStats[normTest.domain].followUpStudents.add(key);
    }

    // Yüksek risk
    if (normTest.riskLevel === 'high') {
        domainStats[normTest.domain].highRiskStudents.add(key);
    }

    // Öncelikli / kritik kayıt
    if (normTest.severity === 'critical') {
        domainStats[normTest.domain].criticalStudents.add(key);
    }
}
     if (
  !studentProfiles[key]
) {

  studentProfiles[key] = {

    class:
      resolvedStudentClass,

    no:
      normalizedStudentNo,

    name:
      resolvedStudentName,

    isCritical:
      false,

    highRiskCount:
      0,

    unknownCount:
      0,

    unknownAlerts:
      [],

    latestAlert:
      normTest
  };
}


// Profil daha eski bir submission üzerinden oluşturulmuş olsa bile
// ekranda öğrencinin GÜNCEL sınıf ve adı gösterilsin.
studentProfiles[key].class =
  resolvedStudentClass;


if (resolvedStudentName) {
  studentProfiles[key].name =
    resolvedStudentName;
}

const getRiskRecordTime =
  record => {

    const value =
      record?.timestamp ??
      record?.createdAt ??
      record?.date ??
      record?.id;


    if (value?.toMillis) {
      return value.toMillis();
    }


    if (value?.seconds) {
      return (
        value.seconds *
        1000
      );
    }


    if (
      typeof value ===
      'number'
    ) {
      return value;
    }


    const parsed =
      Date.parse(value);


    return Number.isFinite(
      parsed
    )
      ? parsed
      : 0;
  };


if (
  getRiskRecordTime(
    normTest
  ) >=
  getRiskRecordTime(
    studentProfiles[key]
      .latestAlert
  )
) {

  studentProfiles[key]
    .latestAlert =
      normTest;
}

      if (normTest.severity === 'critical') studentProfiles[key].isCritical = true;
      if (normTest.riskLevel === 'high') studentProfiles[key].highRiskCount++;
      if (normTest.riskLevel === 'unknown') {
    studentProfiles[key].unknownCount++;
    studentProfiles[key].unknownAlerts.push(normTest);
}

      if (domains[normTest.domain]) {
          domains[normTest.domain].activeCount++;
          let mockScore = normTest.riskLevel === 'high' ? 80 : (normTest.riskLevel === 'medium' ? 50 : 20);
          domains[normTest.domain].totalRisk += mockScore;
      }
    });

    const allProfiles = Object.values(studentProfiles);
    const criticalStudents = allProfiles.filter(p => p.isCritical);
    const highRiskStudents = allProfiles.filter(p => !p.isCritical && p.highRiskCount > 0);
    const quarantineStudents = allProfiles.filter(p => p.unknownCount > 0 && !p.isCritical && p.highRiskCount === 0);

   const radarList = [...criticalStudents, ...highRiskStudents].sort((a, b) => {
        if (a.isCritical && !b.isCritical) return -1;
        if (!a.isCritical && b.isCritical) return 1;
        return b.highRiskCount - a.highRiskCount;
    }).slice(0, 5);

    // 🚀 YENİ: Hayalet ve Tükenmişlik Süzgeci
    let ghostStudents = [];
    let burnoutStudents = [];

    Object.keys(academicActivity).forEach(no => {
        const data = academicActivity[no];
        
        // 1. Hayalet Öğrenci (Ghost) Kontrolü: 5 Gündür Aktivite Yoksa!
       if (data.lastActivity > 0 && (SIMDI - data.lastActivity) > BES_GUN) {

    const isAcknowledged =
        Boolean(data.lastActivityAckAt) ||
        acknowledgedGhostIds.includes(data.lastActivitySubId);

    if (!isAcknowledged) {
        ghostStudents.push({
            no,
            name: data.name,
            class: data.class,
            daysInactive: Math.floor(
                (SIMDI - data.lastActivity) / (1000 * 60 * 60 * 24)
            ),
            lastActivitySubId: data.lastActivitySubId
        });
    }
}

        // 2. Tükenmişlik Radarı Kontrolü: Efor artmış ama Net düşmüşse!
        // Kriter: Son 7 günde 300+ soru çözmüş ancak son denemesi bir öncekine göre 2+ net düşmüş.
        if (data.toplamSoru >= 300 && data.denemeler.length >= 2) {
            const sortedDenemeler = data.denemeler.sort((a,b) => a.time - b.time);
            const last = sortedDenemeler[sortedDenemeler.length - 1];
            const prev = sortedDenemeler[sortedDenemeler.length - 2];
            
            if (last.net < prev.net - 2) { 
                burnoutStudents.push({ no, name: data.name, class: data.class, efor: data.toplamSoru, netDrop: (prev.net - last.net).toFixed(1) });
            }
        }
    });
return {
    registeredStudentCount: registeredStudents.size,
    activeParticipantsCount: activeParticipants.size,
    pendingCount,
        criticalCount: criticalStudents.length,
        highRiskCount: highRiskStudents.length,
        quarantineCount: quarantineStudents.length,
quarantineList: quarantineStudents,
radarList,
        domains,
        domainStats: Object.fromEntries(
    Object.entries(domainStats).map(([key, stats]) => [
        key,
        {
            evaluatedCount: stats.evaluatedStudents.size,
            followUpCount: stats.followUpStudents.size,
            highRiskCount: [...stats.highRiskStudents]
    .filter(studentKey => !stats.criticalStudents.has(studentKey))
    .length,

criticalCount: stats.criticalStudents.size
        }
    ])
),
        ghostCount: ghostStudents.length,
        ghostList: ghostStudents,
        burnoutCount: burnoutStudents.length,
        burnoutList: burnoutStudents
    };
 }, [submissions, dismissedIds, allClasses, acknowledgedGhostIds]);

  return (
    <div className="space-y-6 no-print animate-in fade-in duration-500">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-2 gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-800 tracking-tight">
            Hoş Geldin, {user?.fullName ? user.fullName.split(' ')[0] : 'Hocam'} 👋
          </h2>
          <p className="text-slate-500 text-sm mt-1 font-medium">Rehber360 Gelişmiş PDR Karar Destek Paneli.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="bg-white px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 flex items-center border border-slate-200 shadow-sm cursor-help" title="Bu kod test linklerinize otomatik gömülüdür.">
             <QrCode className="w-4 h-4 mr-2 text-indigo-500"/>
             Sistem Kodu: <span className="text-indigo-700 ml-1 bg-indigo-50 px-2 py-0.5 rounded-md">{user?.refCode || '---'}</span>
          </div>
          {!user?.isPremium && (
            <button onClick={() => setView('pricing')} className="bg-gradient-to-r from-amber-200 to-amber-300 text-amber-800 hover:from-amber-300 hover:to-amber-400 transition-all px-4 py-2.5 rounded-xl text-xs font-bold shadow-sm flex items-center">
              <Star className="h-4 w-4 mr-1 fill-amber-700"/> Premium'a Geç
            </button>
          )}
        </div>
      </div>

      {/* METRİK KARTLARI (5'li Grid Yapısına Geçildi) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
  <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-blue-100 text-blue-600 shrink-0">
    <Users className="h-6 w-6"/>
  </div>

  <div>
    <div className="flex items-center gap-1.5 mb-0.5">
      <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">
        Kayıtlı Öğrenci
      </p>

      <InfoHint
        title="Kayıtlı Öğrenci"
        description="Seçili eğitim öğretim yılında Sınıf Yönetimi'ne tanımlanmış benzersiz öğrenci sayısını gösterir."
      />
    </div>

    <p className="text-2xl font-black text-slate-800 leading-none">
      {dashboardData.registeredStudentCount}
    </p>
  </div>
</div>

        <div className={`p-4 rounded-2xl shadow-sm border flex items-center gap-4 ${dashboardData.criticalCount > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100'}`}>
          <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${dashboardData.criticalCount > 0 ? 'bg-red-600 text-white animate-pulse' : 'bg-slate-100 text-slate-400'}`}><AlertOctagon className="h-6 w-6"/></div>
          <div>
            <p className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${dashboardData.criticalCount > 0 ? 'text-red-700' : 'text-slate-400'}`}>Öncelikli Vaka</p>
            <p className={`text-2xl font-black leading-none ${dashboardData.criticalCount > 0 ? 'text-red-700' : 'text-slate-800'}`}>{dashboardData.criticalCount}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-rose-100 text-rose-600 shrink-0"><AlertTriangle className="h-6 w-6"/></div>
          <div>
            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-0.5">Yüksek Risk</p>
            <p className="text-2xl font-black text-slate-800 leading-none">{dashboardData.highRiskCount}</p>
          </div>
        </div>

        {/* 🚀 YENİ: Hayalet Öğrenci Metriği */}
        <div className={`p-4 rounded-2xl shadow-sm border flex items-center gap-4 ${dashboardData.ghostCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-100'}`}>
          <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${dashboardData.ghostCount > 0 ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'}`}><Archive className="h-6 w-6"/></div>
          <div>
            <p className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${dashboardData.ghostCount > 0 ? 'text-amber-700' : 'text-slate-400'}`}>Sistemden Kopan</p>
            <p className={`text-2xl font-black leading-none ${dashboardData.ghostCount > 0 ? 'text-amber-700' : 'text-slate-800'}`}>{dashboardData.ghostCount}</p>
          </div>
        </div>

       <div
  role={dashboardData.quarantineCount > 0 ? "button" : undefined}
  tabIndex={dashboardData.quarantineCount > 0 ? 0 : -1}
  aria-expanded={showQuarantine}
  onClick={() => {
    if (dashboardData.quarantineCount > 0) {
      setShowQuarantine(prev => !prev);
    }
  }}
  onKeyDown={(e) => {
    if (
      dashboardData.quarantineCount > 0 &&
      (e.key === "Enter" || e.key === " ")
    ) {
      e.preventDefault();
      setShowQuarantine(prev => !prev);
    }
  }}
  className={`p-4 rounded-2xl shadow-sm border flex items-center gap-4 transition-all ${
    dashboardData.quarantineCount > 0
      ? "bg-white border-slate-200 hover:border-slate-300 hover:shadow-md cursor-pointer"
      : "bg-white border-slate-100 cursor-default"
  }`}
>
  <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500 shrink-0">
    <ShieldAlert className="h-6 w-6"/>
  </div>

  <div className="min-w-0">
    <div className="flex items-center gap-1.5 mb-0.5">

      <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">
        Kontrol Gereken
      </p>

      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <InfoHint
          title="Kontrol Gereken Veri"
          description="Analizi tamamlanmış olmasına rağmen risk düzeyi sistem tarafından net olarak sınıflandırılamayan öğrenci kayıtlarını gösterir. Bu kayıtlar doğrudan risk anlamına gelmez; veri veya analiz sonucunun uzman tarafından kontrol edilmesi önerilir."
        />
      </div>

    </div>

    <p className="text-2xl font-black text-slate-800 leading-none">
      {dashboardData.quarantineCount}
    </p>
  </div>

  {dashboardData.quarantineCount > 0 && (
    <ChevronDown
      className={`w-4 h-4 ml-auto text-slate-400 shrink-0 transition-transform ${
        showQuarantine ? "rotate-180" : ""
      }`}
    />
  )}
</div>
      </div>

   {showQuarantine && dashboardData.quarantineCount > 0 && (
  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">

    <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
      <div>
        <h4 className="text-sm font-bold text-slate-800">
          Kontrol Gereken Kayıtlar
        </h4>

        <p className="text-[11px] text-slate-400 mt-0.5">
          Risk düzeyi otomatik olarak sınıflandırılamayan kayıtlar
        </p>
      </div>

      <span className="text-[10px] font-bold bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full">
        {dashboardData.quarantineCount} öğrenci
      </span>
    </div>

    <div className="divide-y divide-slate-100">
      {dashboardData.quarantineList.map(student => {

        const testNames = [
          ...new Set(
            (student.unknownAlerts || []).map(
              alert => alert.testName || alert.testId || 'Bilinmeyen Envanter'
            )
          )
        ];

        return (
          <div
            key={`${student.class}_${student.no}`}
            className="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
          >
            <div>
              <p className="text-xs font-bold text-slate-700">
                {student.class} - {student.no}
                {student.name && (
                  <span className="font-medium text-slate-500">
                    {' '}| {student.name}
                  </span>
                )}
              </p>

              <p className="text-[10px] text-slate-400 mt-1">
                Sistem bu öğrencinin aşağıdaki kayıtlarında net bir risk sınıfı oluşturamadı.
              </p>
            </div>

            <div className="flex flex-wrap gap-1.5 sm:justify-end">
              {testNames.map(testName => (
                <span
                  key={testName}
                  className="text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200 px-2 py-1 rounded-lg"
                >
                  {testName}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>

  </div>
)}

      {/* ANA KOKPİT */}
      <div className="grid lg:grid-cols-3 gap-6">
        
        <div className="lg:col-span-2 flex flex-col gap-6">
          
          {/* AI İÇGÖRÜ KARTI (Mor Kart) */}
          <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-[2rem] p-6 md:p-8 relative overflow-hidden shadow-lg border border-indigo-800 flex flex-col justify-between h-full">
            <div className="absolute top-0 right-0 p-6 opacity-10 pointer-events-none"><Brain className="w-32 h-32 text-indigo-200" /></div>
            <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-pulse"></div>

            <div className="relative z-10 flex flex-col h-full justify-center">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center text-indigo-200 text-xs font-bold uppercase tracking-widest">
                  <Sparkles className="w-4 h-4 mr-2 text-purple-300"/> PDR Akıllı Karar Destek Sistemi
                </div>
                <div className="flex items-center bg-indigo-950/50 px-3 py-1.5 rounded-full border border-indigo-800/50 shadow-sm">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse mr-2 shadow-[0_0_8px_rgba(52,211,153,0.8)]"></div>
                  <span className="text-[10px] font-bold text-indigo-200 tracking-wider">Sistem Aktif</span>
                </div>
              </div>
              
              {/* 🚀 YENİ: Tükenmişlik ve Hayalet Öğrenci Fısıltı Motoru */}
              {dashboardData.criticalCount > 0 ? (
  <>
    <div className="flex items-center gap-2 mb-3">
      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-red-500/20 border border-red-400/30 text-red-200 text-[10px] font-black uppercase tracking-widest">
        Öncelik 1
      </span>
    </div>

    <h3 className="text-white text-xl md:text-2xl font-black mb-3 leading-snug">
      <span className="text-red-300">
        {dashboardData.criticalCount} öncelikli öğrenci kaydı
      </span>{' '}
      dikkatinizi bekliyor.
    </h3>

    <p className="text-indigo-100 text-sm mb-6 max-w-xl leading-relaxed">
      Risk taramalarında öncelikli değerlendirme gerektiren öğrenci kayıtları
      bulunuyor. Önce bu öğrencileri incelemeniz önerilir.
    </p>

    <button
      onClick={handleOpenRiskRadar}
      className="bg-red-500 hover:bg-red-400 text-white px-6 py-3 rounded-xl text-sm font-bold shadow-lg transition-all flex items-center w-fit"
    >
      <AlertOctagon className="w-4 h-4 mr-2" />
      Öncelikli Vakaları İncele
    </button>
  </>

) : dashboardData.highRiskCount > 0 ? (
  <>
    <div className="flex items-center gap-2 mb-3">
      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-rose-500/20 border border-rose-400/30 text-rose-200 text-[10px] font-black uppercase tracking-widest">
        Öncelik 2
      </span>
    </div>

    <h3 className="text-white text-xl md:text-2xl font-black mb-3 leading-snug">
      <span className="text-rose-300">
        {dashboardData.highRiskCount} öğrencide yüksek risk sinyali
      </span>{' '}
      bulunuyor.
    </h3>

    <p className="text-indigo-100 text-sm mb-6 max-w-xl leading-relaxed">
      Bir veya daha fazla risk envanterinde yüksek düzeyde sinyal veren
      öğrenciler bulunuyor. Öğrenci bazlı sonuçları inceleyebilirsiniz.
    </p>

    <button
      onClick={handleOpenRiskRadar}
      className="bg-white text-indigo-900 hover:bg-rose-50 px-6 py-3 rounded-xl text-sm font-bold shadow-lg transition-all flex items-center w-fit"
    >
      <ShieldAlert className="w-4 h-4 mr-2 text-rose-500" />
      Risk Radarını Aç
    </button>
  </>

) : dashboardData.pendingCount > 0 ? (
  <>
    <div className="flex items-center gap-2 mb-3">
      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-purple-500/20 border border-purple-400/30 text-purple-200 text-[10px] font-black uppercase tracking-widest">
        Öncelik 3
      </span>
    </div>

    <h3 className="text-white text-xl md:text-2xl font-black mb-3 leading-snug">
      <span className="text-purple-300">
        {dashboardData.pendingCount} yeni envanter
      </span>{' '}
      analiz edilmeyi bekliyor.
    </h3>

    <p className="text-indigo-200 text-sm mb-6 max-w-xl leading-relaxed">
      Yeni öğrenci verilerini inceleyerek bireysel ve kurumsal değerlendirmeleri
      güncel tutabilirsiniz.
    </p>

    <button
      onClick={() => setView('class-analysis')}
      className="bg-white text-indigo-900 hover:bg-indigo-50 px-6 py-3 rounded-xl text-sm font-bold shadow-lg transition-all flex items-center w-fit"
    >
      <Activity className="w-4 h-4 mr-2" />
      Kurumsal Analizi Aç
    </button>
  </>

) : dashboardData.burnoutCount > 0 || dashboardData.ghostCount > 0 ? (
                <>
  <div className="flex items-center gap-2 mb-3">
    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-200 text-[10px] font-black uppercase tracking-widest">
      Öncelik 4
    </span>
  </div>

  <h3 className="text-white text-xl md:text-2xl font-black mb-3 leading-snug">
    <span className="text-amber-300">Akademik takip sinyalleri</span> tespit edildi.
  </h3>
                  <div className="space-y-3 mb-6 max-w-xl relative z-20">
                    {dashboardData.burnoutCount > 0 && (
                      <div className="bg-indigo-950/40 rounded-xl border border-indigo-800/50 shadow-sm overflow-hidden transition-all">
                        <button onClick={() => setShowBurnout(!showBurnout)} className="w-full flex items-start p-3 text-left hover:bg-indigo-900/40 transition">
                          <Zap className="w-5 h-5 mr-3 text-amber-400 shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-amber-400 text-xs font-black uppercase tracking-wider flex items-center justify-between">
                              Efor–Verim Uyumsuzluğu ({dashboardData.burnoutCount} Öğrenci)
                              <ChevronDown className={`w-4 h-4 transition-transform ${showBurnout ? 'rotate-180' : ''}`} />
                            </p>
                           <p className="text-indigo-100 text-[11px] font-medium mt-1">
  Son dönemde yüksek çalışma eforuna rağmen deneme performansı düşen öğrenciler. İncelemek için tıklayın.
</p>
                          </div>
                        </button>
                        {showBurnout && (
                          <div className="p-3 bg-indigo-950/60 border-t border-indigo-800/50 max-h-40 overflow-y-auto custom-scrollbar">
                            {dashboardData.burnoutList.map((st, idx) => (
                              <div key={idx} className="flex justify-between items-center py-2 border-b border-indigo-800/30 last:border-0">
                                <span className="text-xs font-bold text-indigo-100">{st.class} - {st.no} {st.name && `(${st.name})`}</span>
                                <span className="text-[10px] bg-rose-500/20 text-rose-300 px-2 py-1 rounded font-bold">-{st.netDrop} Net</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {dashboardData.ghostCount > 0 && (
                      <div className="bg-indigo-950/40 rounded-xl border border-indigo-800/50 shadow-sm overflow-hidden transition-all">
                        <button onClick={() => setShowGhost(!showGhost)} className="w-full flex items-start p-3 text-left hover:bg-indigo-900/40 transition">
                          <Archive className="w-5 h-5 mr-3 text-slate-400 shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-slate-300 text-xs font-black uppercase tracking-wider flex items-center justify-between">
                              Sistemden Kopanlar ({dashboardData.ghostCount} Öğrenci)
                              <ChevronDown className={`w-4 h-4 transition-transform ${showGhost ? 'rotate-180' : ''}`} />
                            </p>
                            <p className="text-indigo-100 text-[11px] font-medium mt-1">5 günden uzun süredir işlem yapmayanlar. İncelemek için tıklayın.</p>
                          </div>
                        </button>
                        {showGhost && (
                          <div className="p-3 bg-indigo-950/60 border-t border-indigo-800/50 max-h-40 overflow-y-auto custom-scrollbar">
                           {dashboardData.ghostList.map((st, idx) => (
  <div
    key={`${st.no}_${st.lastActivitySubId || idx}`}
    className="flex items-center justify-between gap-3 py-2 border-b border-indigo-800/30 last:border-0"
  >
    <div className="min-w-0">
      <span className="text-xs font-bold text-indigo-100 block truncate">
        {st.class} - {st.no} {st.name && `(${st.name})`}
      </span>

      <span className="text-[10px] text-slate-400">
        {st.daysInactive} gündür öğrenci aktivitesi yok
      </span>
    </div>

    <button
      onClick={() => handleAcknowledgeGhost(st)}
      className="shrink-0 text-[10px] bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/20 px-2.5 py-1.5 rounded-lg font-bold transition flex items-center"
      title="Uyarıyı gördüm / öğrenciyi takibe aldım"
    >
      <Check className="w-3 h-3 mr-1" />
      Takibe Alındı
    </button>
  </div>
))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setView('coaching')} className="bg-white text-indigo-900 hover:bg-indigo-50 px-5 py-2.5 rounded-xl text-sm font-bold shadow-[0_0_15px_rgba(255,255,255,0.2)] hover:shadow-[0_0_25px_rgba(255,255,255,0.5)] transition-all flex items-center w-fit">
                      <Target className="w-4 h-4 mr-2"/> Koçluk Paneline Git
                    </button>
                  </div>
                </>
                ) : (

<>
              
                  <h3 className="text-white text-xl md:text-2xl font-black mb-4 leading-snug">
                    Tüm sistemler stabil. 🎯 <br/>Riskler anlık olarak izleniyor.
                  </h3>
                  <div className="space-y-3 mb-6 max-w-xl">
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 mr-2 text-emerald-400 shrink-0 mt-0.5" />
                      <p className="text-indigo-100 text-sm font-medium">Öğrencilerin akademik efor ve performans verilerinde belirgin bir uyumsuzluk sinyali görünmüyor.</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 mr-2 text-emerald-400 shrink-0 mt-0.5" />
                      <p className="text-indigo-100 text-sm font-medium">Yeni nesil "Alan Bazlı" raporlamalar için Kurumsal Analiz modülünü kullanın.</p>
                    </div>
                  </div>
                  <button onClick={() => setView('class-analysis')} className="bg-white text-indigo-900 hover:bg-indigo-50 px-6 py-3 rounded-xl text-sm font-bold shadow-[0_0_15px_rgba(255,255,255,0.2)] hover:shadow-[0_0_25px_rgba(255,255,255,0.5)] transition-all flex items-center w-fit">
                    <Activity className="w-4 h-4 mr-2"/> Sınıf Risk Haritaları
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* OKULUN PSİKOSOSYAL PROFİLİ */}
<div className="bg-white p-6 md:p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col h-full min-h-[300px]">

  <div className="flex justify-between items-start mb-5">
    <div>
      <h3 className="font-bold text-slate-800 text-sm flex items-center">
        <HeartPulse className="h-5 w-5 mr-2 text-rose-500"/>
        Okulun Psikososyal Profili
      </h3>

      <p className="text-[10px] text-slate-400 mt-1 font-medium">
        Aktif öğrenci sinyallerinin alan bazlı görünümü
      </p>
    </div>
  </div>

  <div className="flex-1 flex flex-col justify-center space-y-5">

    {Object.entries(dashboardData.domains).map(([key, dom]) => {

      const stats = dashboardData.domainStats?.[key] || {
        evaluatedCount: 0,
        followUpCount: 0,
        highRiskCount: 0,
        criticalCount: 0
      };

      const followUpRate =
        stats.evaluatedCount > 0
          ? Math.round(
              (stats.followUpCount / stats.evaluatedCount) * 100
            )
          : 0;

      return (
        <div
          key={key}
          className="border-b border-slate-100 last:border-0 pb-4 last:pb-0"
        >

          {/* Alan Başlığı */}
          <div className="flex justify-between items-center mb-2">

            <div className="flex items-center gap-1.5">
  <span className="text-xs font-bold text-slate-700">
    {dom.label} Alanı
  </span>

  <InfoHint
    title={DOMAIN_INFO[key]?.title}
    description={DOMAIN_INFO[key]?.description}
    items={DOMAIN_INFO[key]?.items}
  />
</div>

            <span className="text-[10px] font-bold text-slate-400">
              {stats.evaluatedCount > 0
                ? `${stats.evaluatedCount} öğrenci değerlendirildi`
                : 'Veri Yok'}
            </span>

          </div>


          {stats.evaluatedCount > 0 ? (
            <>

              {/* Takip Yoğunluğu */}
              <div className="flex items-center gap-3 mb-2">

                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">

                  <div
                    className={`${dom.color} h-full rounded-full transition-all duration-1000`}
                    style={{
                      width: `${Math.min(followUpRate, 100)}%`
                    }}
                  ></div>

                </div>

                <span className="text-[10px] font-black text-slate-500 min-w-[34px] text-right">
                  %{followUpRate}
                </span>

              </div>


              {/* Sayısal Özet */}
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-bold">

                <span className="text-amber-600">
                  {stats.followUpCount} takip
                </span>

                <span className="text-rose-600">
                  {stats.highRiskCount} yüksek
                </span>

                <span className={
                  stats.criticalCount > 0
                    ? "text-red-700"
                    : "text-slate-400"
                }>
                  {stats.criticalCount} öncelikli
                </span>

              </div>

            </>
          ) : (

            <div className="h-2 bg-slate-100 rounded-full"></div>

          )}

        </div>
      );
    })}

  </div>


  <div className="mt-5 pt-4 border-t border-slate-100">

    <p className="text-[9px] text-slate-400 text-center leading-relaxed font-medium">
      Yüzde, ilgili alanda değerlendirilen benzersiz öğrenciler içinde
      aktif takip sinyali bulunan öğrencilerin oranını gösterir.
    </p>

  </div>

</div>

      </div>

      {/* ALT PANEL */}
      <div className="grid lg:grid-cols-3 gap-6">
          
        {/* RİSK RADARI */}
        <div
  id="dashboard-risk-radar"
  className="lg:col-span-2 bg-white rounded-[2rem] p-6 border border-slate-200 shadow-sm flex flex-col scroll-mt-6"
>
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-slate-800 text-sm flex items-center">
                    <ShieldAlert className="w-5 h-5 mr-2 text-rose-500"/> Öncelikli Risk Radarı
                </h3>
                {dashboardData.radarList.length > 0 && (
                   <button onClick={() => setShowRadar(!showRadar)} className="text-[10px] font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 px-3 py-1.5 rounded-lg border border-rose-200 flex items-center transition">
                       {showRadar ? 'Listeyi Gizle' : `Listeyi Göster (${dashboardData.radarList.length} Kişi)`} <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showRadar ? 'rotate-180' : ''}`} />
                   </button>
                )}
            </div>
            
            <div className="flex-1 space-y-3">
            {dashboardData.radarList.length > 0 ? (
                !showRadar ? (
                   <div className="h-full flex flex-col items-center justify-center py-8 text-center border-2 border-dashed border-slate-100 rounded-xl bg-slate-50">
                       <ShieldAlert className="w-8 h-8 text-slate-300 mb-2" />
                       <p className="text-xs font-bold text-slate-500">Öğrenci gizliliği nedeniyle liste gizlenmiştir.<br/>Görüntülemek için sağ üstteki butona tıklayın.</p>
                   </div>
                ) : (
                  dashboardData.radarList.map(student => (
                  <div key={`${student.class}_${student.no}`} className={`p-4 border rounded-xl flex flex-col gap-3 transition-all ${student.isCritical ? 'bg-red-50 border-red-300 shadow-sm' : 'bg-slate-50 border-slate-100 hover:border-slate-300'}`}>
                      <div className="flex items-start gap-3">
                          <div className={`mt-0.5 h-3 w-3 rounded-full shrink-0 ${student.isCritical ? 'bg-red-600 animate-pulse shadow-[0_0_8px_rgba(220,38,38,0.8)]' : 'bg-rose-400'}`}></div>
                          <div className="flex-1">
                              <p className="text-xs font-bold text-slate-800">
                                  {student.class} - {student.no} {student.name && <span className="text-slate-500 font-medium">| {student.name}</span>}
                              </p>
                              <p className="text-[10px] text-slate-500 mt-1 font-medium">
                                  {student.isCritical ? (
                                      <span className="text-red-700 font-bold bg-red-100 px-1.5 py-0.5 rounded">🚨 ACİL MÜDAHALE (18. Madde vb.)</span>
                                  ) : (
                                      <span className="text-rose-600 font-bold bg-rose-50 px-1.5 py-0.5 rounded">{student.highRiskCount} Farklı Envanterde Yüksek Risk</span>
                                  )}
                                  {' '}Son Kayıt: {student.latestAlert?.testName}
                              </p>
                          </div>
                          <button 
                              onClick={() => handleResolveRisk(student.latestAlert?.firebaseId || student.latestAlert?.id)} 
                              title="Görüldü / İşlem Yapıldı" 
                              className="h-8 w-8 rounded-full bg-white border border-slate-200 text-slate-400 flex items-center justify-center hover:bg-emerald-100 hover:text-emerald-600 hover:border-emerald-200 transition-colors shrink-0 shadow-sm"
                          >
                              <Check className="h-4 w-4" />
                          </button>
                      </div>

                      <div className="flex flex-wrap gap-2 pl-6 mt-1">
                          {/* 🎯 DOĞRUDAN DOSYAYI AÇAN BUTON */}
                          <button onClick={() => onAnalyze(student.latestAlert)} className="text-[10px] font-bold bg-white text-blue-700 border border-blue-200 hover:bg-blue-600 hover:text-white px-3 py-1.5 rounded-lg transition-colors flex items-center shadow-sm">
                              <FileText className="w-3.5 h-3.5 mr-1.5"/> İncele
                          </button>
                          {/* 🎯 İÇERİK ATÖLYESİNE GÖTÜREN BUTON */}
                          <button onClick={() => setView('ai-workshop')} className="text-[10px] font-bold bg-white text-purple-700 border border-purple-200 hover:bg-purple-600 hover:text-white px-3 py-1.5 rounded-lg transition-colors flex items-center shadow-sm">
                              <Brain className="w-3.5 h-3.5 mr-1.5"/> AI İçerik Atölyesi
                          </button>
                      </div>
                  </div>
                  ))
                )
            ) : (
                <div className="h-full flex flex-col items-center justify-center py-6 text-center">
                <CheckCircle className="w-10 h-10 text-emerald-400 mb-2 opacity-50"/>
                <p className="text-xs font-bold text-slate-500">Müdahale bekleyen<br/>vaka bulunmuyor.</p>
                </div>
            )}
            </div>
        </div>

        {/* HIZLI AKSİYONLAR (4 BUTON GERİ GELDİ) */}
        <div className="bg-white rounded-[2rem] p-6 border border-slate-200 shadow-sm flex flex-col justify-between">
            <div>
                <h3 className="font-bold text-slate-800 text-sm mb-4 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-amber-500"/> Hızlı Aksiyonlar
                </h3>
                <div className="space-y-2">
                    <button onClick={() => setView('ai-workshop')} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-purple-50 border border-slate-100 hover:border-purple-200 rounded-xl transition-all group">
                        <div className="flex items-center gap-3">
                        <Brain className="w-5 h-5 text-purple-600"/>
                        <span className="text-xs font-bold text-slate-700 group-hover:text-purple-700">⚡ AI İçerik Atölyesi</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-transform"/>
                    </button>
                    
                    <button onClick={() => setView('materials')} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-blue-50 border border-slate-100 hover:border-blue-200 rounded-xl transition-all group">
                        <div className="flex items-center gap-3">
                        <Archive className="w-5 h-5 text-blue-600"/>
                        <span className="text-xs font-bold text-slate-700 group-hover:text-blue-700">📂 Hazır Materyal & Formlar</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-transform"/>
                    </button>

                    <button onClick={() => setView('tests')} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-emerald-50 border border-slate-100 hover:border-emerald-200 rounded-xl transition-all group">
                        <div className="flex items-center gap-3">
                        <ClipboardList className="h-5 w-5 text-emerald-600"/>
                        <span className="text-xs font-bold text-slate-700 group-hover:text-emerald-700">🔗 Öğrencilere Test Gönder</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-transform"/>
                    </button>

                    <button onClick={() => setView('student-folders')} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-amber-50 border border-slate-100 hover:border-amber-200 rounded-xl transition-all group">
                        <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-amber-600"/>
                        <span className="text-xs font-bold text-slate-700 group-hover:text-amber-700">👤 Öğrenci Dosyaları (CRM)</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-transform"/>
                    </button>
                </div>
            </div>
            
            {/* Canlı Akışa Yönlendirme */}
            <div className="mt-6 pt-4 border-t border-slate-100">
                <p className="text-[10px] text-slate-400 font-medium mb-3 text-center">Çözülen tüm envanterleri tarih sırasına göre incelemek ister misiniz?</p>
                <button onClick={() => setView('students')} className="w-full text-center py-2 text-xs font-bold text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-200 rounded-xl transition-colors shadow-sm">
                    Canlı Veri Akışına Git &rarr;
                </button>
            </div>
        </div>

      </div>
    </div>
  ); 
};

export default DashboardHome;